
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface StatsResult {
  customers: number;
  appointments: number;
  services: number;
  revenue: number;
  bookingsByDay: { day: string; count: number }[];
  servicePerformance: { name: string; bookings: number }[];
  staffPerformance: { name: string; bookings: number; utilization: number }[];
  timeDistribution: { hour: number; count: number }[];
  weekdayDistribution: { day: string; count: number }[];
}

export interface StatsOptions {
  period?: 'day' | 'week' | 'month' | 'year' | 'all';
  studioId?: string;
  fromDate?: string;
  toDate?: string;
}

export function useStudioStats(options: StatsOptions = {}) {
  const [stats, setStats] = useState<StatsResult>({
    customers: 0,
    appointments: 0,
    services: 0,
    revenue: 0,
    bookingsByDay: [],
    servicePerformance: [],
    staffPerformance: [],
    timeDistribution: [],
    weekdayDistribution: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  
  const { period = 'month', studioId, fromDate, toDate } = options;
  
  const fetchStudioStats = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // This is a placeholder for API integration
      // In a real app, we would call an RPC function or endpoint
      
      // For now, let's simulate with mock data based on period and studioId
      const mockMultiplier = 
        period === 'day' ? 1 :
        period === 'week' ? 7 :
        period === 'month' ? 30 :
        period === 'year' ? 365 : 500;
      
      // Simulate loading time
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Generate realistic mock data
      const baseCustomers = studioId ? 15 : 50; // Individual studio has fewer customers than the whole platform
      const baseAppointments = studioId ? 25 : 80; 
      const baseRevenue = studioId ? 800 : 3000;
      
      // Generate daily booking data for the last 30 days
      const today = new Date();
      const bookingsByDay = Array.from({ length: 30 }, (_, i) => {
        const date = new Date();
        date.setDate(today.getDate() - 29 + i);
        const day = date.toISOString().split('T')[0];
        return {
          day,
          count: Math.floor((baseAppointments / 30) * (0.5 + Math.random()))
        };
      });
      
      // Generate service performance data
      const serviceNames = ["Haarschnitt", "Färben", "Styling", "Rasur", "Waschen", "Beratung"];
      const servicePerformance = serviceNames.map(name => ({
        name,
        bookings: Math.floor(baseAppointments * (0.05 + Math.random() * 0.25))
      })).sort((a, b) => b.bookings - a.bookings);
      
      // Generate staff performance data
      const staffNames = ["Anna", "Michael", "Sophie", "Thomas", "Julia"];
      const staffPerformance = staffNames.map(name => ({
        name,
        bookings: Math.floor(baseAppointments * (0.1 + Math.random() * 0.3)),
        utilization: Math.floor(40 + Math.random() * 55) // percentage
      })).sort((a, b) => b.bookings - a.bookings);
      
      // Generate time distribution (bookings per hour)
      const timeDistribution = Array.from({ length: 12 }, (_, i) => {
        const hour = i + 9; // 9:00 - 21:00
        return {
          hour,
          count: Math.floor(baseAppointments * (hour >= 12 && hour <= 18 ? 
            0.1 + Math.random() * 0.15 : 0.02 + Math.random() * 0.08))
        };
      });
      
      // Generate weekday distribution
      const weekdays = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
      const weekdayDistribution = weekdays.map(day => ({
        day,
        count: Math.floor(baseAppointments * (day === "Sa" ? 0.2 + Math.random() * 0.2 :
          day === "So" ? 0.05 + Math.random() * 0.1 : 0.1 + Math.random() * 0.15))
      }));
      
      setStats({
        customers: Math.floor(baseCustomers * (0.8 + Math.random() * 0.4)),
        appointments: Math.floor(baseAppointments * (0.8 + Math.random() * 0.4)),
        services: servicePerformance.length,
        revenue: Math.floor(baseRevenue * (0.8 + Math.random() * 0.4)),
        bookingsByDay,
        servicePerformance,
        staffPerformance,
        timeDistribution,
        weekdayDistribution
      });
    } catch (err: any) {
      console.error("Error fetching studio stats:", err);
      setError(err instanceof Error ? err : new Error(err.message || "Unknown error occurred"));
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    fetchStudioStats();
  }, [period, studioId, fromDate, toDate]);
  
  return { stats, isLoading, error, fetchStudioStats };
}
